import { DRIVER_TYPE, SuggestionOutput, VehicleOutput } from '@allocab/allocab-commons-shared-types';
import { useIsFetching } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { VehicleSelectControlled } from './_authenticated._main.drivers.add/VehicleSelectControlled';
import { Label } from '../components/atomic/Label';
import { RadioButton } from '../components/atomic/RadioButton';
import { TextInput } from '../components/atomic/TextInput';
import { PrimaryButton } from '../components/button/PrimaryButton';
import { PhoneInputControlled } from '../components/form/PhoneInputControlled';
import { SuggestionsInputControlled } from '../components/form/SuggestionsInputControlled';
import { useCreateDriver } from '../hooks/useCreateDriver';
import { queryKeys } from '../services/datastore/queryKeys';
import { t } from '../services/translation/translation';
import { generateRandomPassword } from '../utils/auth';
import { rules } from '../utils/validation';

type FieldValues = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  phoneNumberCountry: string;
  address: SuggestionOutput;
  driverType: DRIVER_TYPE;
  vehicle?: VehicleOutput;
};

export const DriverAddRoute = () => {
  const navigate = useNavigate();
  const { mutate: createDriver, isLoading } = useCreateDriver();
  const isFetchingUser = useIsFetching(queryKeys.user.me()) > 0;
  const methods = useForm<FieldValues>({ defaultValues: { driverType: 'PRIVATE_DRIVER' } });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const onSubmit = handleSubmit(
    ({ firstName, lastName, email, phoneNumber, phoneNumberCountry, address, driverType, vehicle }) => {
      createDriver(
        {
          gender: 'MALE',
          firstName,
          lastName,
          email,
          password: generateRandomPassword(),
          phoneNumber,
          phoneNumberCountry,
          address,
          driverType,
          vehicleId: vehicle?.id,
        },
        { onSuccess: driver => navigate(`/drivers/${driver.id}`) },
      );
    },
  );

  return (
    <div>
      <Helmet>
        <title>{t('driver-add-route.title')} - Allocab</title>
      </Helmet>
      <h3 className="text-3xl font-bold dark:text-white mb-6">{t('driver-add-route.title')}</h3>

      <div className="w-full bg-white rounded-lg shadow dark:border md:mt-0 sm:max-w-lg xl:p-0 dark:bg-gray-800 dark:border-gray-700">
        <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
          <form className="space-y-4" onSubmit={onSubmit}>
            <FormProvider {...methods}>
              <Label>{t('common.driver-type')}</Label>
              <div className="flex items-center gap-2">
                <RadioButton
                  id="PRIVATE_DRIVER"
                  {...register('driverType')}
                  label={t('common.vtc')}
                  value="PRIVATE_DRIVER"
                />
                <RadioButton id="TAXI" {...register('driverType')} label={t('common.taxi')} value="TAXI" />
                <RadioButton
                  id="MOTO_TAXI"
                  {...register('driverType')}
                  label={t('common.moto-taxi')}
                  value="MOTO_TAXI"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <TextInput
                  type="firstName"
                  id="firstName"
                  placeholder="Jean"
                  label={t('common.first-name')}
                  errorMessage={errors.firstName?.message}
                  {...register('firstName', { required: rules.required })}
                />
                <TextInput
                  type="lastName"
                  id="lastName"
                  placeholder="Dupont"
                  label={t('common.last-name')}
                  errorMessage={errors.lastName?.message}
                  {...register('lastName', { required: rules.required })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <TextInput
                  type="email"
                  id="email"
                  placeholder="jean@dupont.com"
                  label={t('common.email')}
                  errorMessage={errors.email?.message}
                  {...register('email', { required: rules.required, validate: rules.email })}
                />
                <PhoneInputControlled
                  name="phoneNumber"
                  countryName="phoneNumberCountry"
                  label={t('common.phone')}
                  rules={{
                    required: rules.required,
                    validate: rules.phone,
                  }}
                />
              </div>

              <SuggestionsInputControlled
                name="address"
                label={t('driver-add-route.hq')}
                info={t('driver-add-route.hq-info')}
                rules={{ required: rules.required }}
              />

              <VehicleSelectControlled name="vehicle" label={t('common.current-vehicle')} />

              <PrimaryButton
                type="submit"
                className="w-full"
                text={t('common.add')}
                isLoading={isLoading || isFetchingUser}
              />
            </FormProvider>
          </form>
        </div>
      </div>
    </div>
  );
};
